"use client"

import { useState, useEffect, useRef } from "react"
import { HttpTypes } from "@medusajs/types"
import * as Dialog from "@radix-ui/react-dialog"
import {
  XMark,
  Adjustments,
  MagnifyingGlass,
  ChevronDown,
  ChevronUpMini,
} from "@medusajs/icons"
import { Button } from "@medusajs/ui"
import SearchFilters from "../search-filters"
import ActiveFilters from "../active-filters"
import { SearchFilters as SearchFiltersType } from "../../templates/search-template"
import { getProductsCount } from "@/lib/data/search"
import type { SearchParams } from "@/lib/data/search"
import { useSearchParams } from "next/navigation"
import { getHierarchicalCategories, getAllCategoryIds } from "@/lib/util/category-helpers"

const defaultFilters: SearchFiltersType = {
  categories: [],
  brands: [],
  subcategories: [],
  packaging: [],
  orderUnit: [],
  ean: [],
  region: [],
  category: [],
  type: [],
  priceRange: [0, 10000],
  inStock: false,
  fastDelivery: false,
  bulkPricing: false,
  programmable: false,
  color: [],
  voltage: [],
  volume: [],
  ipProtection: [],
  connectivity: [],
  gnSize: [],
  material: [],
  mainMaterial: [],
  zmogljivost: [],
  proizvodnaZmogljivost: [],
  klimatskiRazdred: [],
  izdelekZlozljiv: [],
  drzavaPorekla: [],
  energiskiRazred: [],
  primeranZaUporabo: [],
  hladilnoSredstvo: [],
  odtocnaPipa: [],
  steviloVentilatorjev: [],
  debelinaRezanja: [],
  tezaBaterije: [],
  dolzinaKabla: [],
  montazoPotrebenElektricar: [],
  mocPlinskegaGorilnika: [],
  tlakPlina: [],
  stopnjaRegulacije: [],
  enotaZaRegulacije: [],
  zaPriklopVodovodar: [],
  osnovniPremer: [],
  dolzinaRezila: [],
  casPripraveVode: [],
  tezaVsebine: [],
  brutoTeza: [],
  netoTeza: [],
  dolzinaRocaja: [],
  premer: [],
  sirina: [],
  dolzina: [],
  visina: [],
  notranjaSirina: [],
  notranjaGlobina: [],
  notranjaVisina: [],
  vhodnaMoc: [],
  izhodnaMoc: [],
  najvecjiVolumen: [],
  najvecjaSirinaZivila: [],
  vrtljajevNaMinuto: [],
  dolzinaGredi: [],
  debelinaIzolacije: [],
  temperaturaOd: [],
  temperaturaDo: [],
  // New attributes
  debelinaMaterial: [],
  odporenNaTempOd: [],
  odporenNaTempDo: [],
  premerZgoraj: [],
  merePredala: [],
  dolzinaXSirina: [],
  premerSpodaj: [],
  sifraIzdelka: [],
  zmogljivostCrpalke: [],
  vrstaIzdelka: [],
  prostorninaVMl: [],
  dolzinaRogljev: [],
  dolzinaXSirinaXVisina: [],
  dolzinaXVisina: [],
  zunanjaDelzinaXSirinaXVisina: [],
  notranjaDelzinaXSirinaXVisina: [],
  velikostZajemalke: [],
  motiv: [],
  casomer: [],
  enotaCasomera: [],
  razmikMedVodiliPladnjev: [],
  uporabnaProstornina: [],
  vrstaKontrolnePlosce: [],
  tehtanjeOd: [],
  tehtanjeDo: [],
  izdelekJePotrebnoSestaviti: [],
  vsebujeBaterijo: [],
  steviloBaterij: [],
  izdelekJeTekoc: [],
  brand: [],
}
import {
  FILTER_TITLE_MAP,
  ArrayFilterKeys,
} from "../search-filters"
import PriceRangeSlider from "../price-range-slider"
import { getAvailablePriceRange } from "@/lib/data/search"

interface MobileFilterDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  filters: SearchFiltersType
  onFiltersChange: (filters: Partial<SearchFiltersType>) => void
  onClearAll: () => void
  categories: HttpTypes.StoreProductCategory[]
  facets?: any
  initialFilterOptions: any
  filterOptions: any
  loading: boolean
  totalResults: number
  onSearch: () => void
  showCategoriesFilter?: boolean
}

interface FilterHeaderProps {
  onClose: () => void
  onSearch: () => void
  totalResults: number
  loading: boolean
}

const FilterHeader = ({ onClose, onSearch, totalResults, loading }: FilterHeaderProps) => (
  <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-10 shadow-sm">
    <div className="flex items-center gap-3">
      <Adjustments className="w-5 h-5 text-gray-600" />
      <Dialog.Title className="text-xl font-semibold text-gray-900">
        Filtri
      </Dialog.Title>
    </div>
    <Dialog.Close asChild>
      <button 
        className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-full hover:bg-gray-100"
        onClick={onClose}
      >
        <XMark className="w-5 h-5" />
      </button>
    </Dialog.Close>
  </div>
)

interface AppliedFiltersSectionProps {
  filters: SearchFiltersType
  onRemoveFilter: (filters: Partial<SearchFiltersType>) => void
  onClearAll: () => void
  categories: HttpTypes.StoreProductCategory[]
}

const AppliedFiltersSection = ({ filters, onRemoveFilter, onClearAll, categories }: AppliedFiltersSectionProps) => {
  // Function to get categories for display with correct subcategory logic
  const getDisplayCategories = () => {
    const { mainCategories, subcategoriesByParent } = getHierarchicalCategories(categories)
    
    // Get all selected category IDs
    const selectedCategoryIds = filters.categories
    
    // Separate main categories and subcategories
    const selectedMainCategories = selectedCategoryIds.filter(id => 
      mainCategories.some(mainCat => mainCat.id === id)
    )
    
    // Get all subcategories from the subcategoriesByParent object
    const allSubCategories = Object.values(subcategoriesByParent).flat()
    
    const selectedSubCategories = selectedCategoryIds.filter(id => 
      allSubCategories.some((subCat: HttpTypes.StoreProductCategory) => subCat.id === id)
    )
    
    // If main categories are selected, don't show their subcategories
    // But still show directly selected subcategories
    const displayCategories = [...selectedMainCategories]
    
    // Add subcategories that are not children of selected main categories
    selectedSubCategories.forEach(subCatId => {
      const subCategory = allSubCategories.find((sub: HttpTypes.StoreProductCategory) => sub.id === subCatId)
      if (subCategory && subCategory.parent_category_id) {
        // Check if this subcategory's parent is not in the selected main categories
        const parentIsSelected = selectedMainCategories.includes(subCategory.parent_category_id)
        if (!parentIsSelected) {
          displayCategories.push(subCatId)
        }
      }
    })
    
    return displayCategories
  }

  // Create a modified filters object that shows categories correctly
  const displayFilters = {
    ...filters,
    categories: getDisplayCategories()
  }

  const hasActiveFilters = Object.values(filters).some(value => {
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'boolean') return value
    if (Array.isArray(value) && value.length === 2) return value[0] > 0 || value[1] < 10000
    return false
  })

  if (!hasActiveFilters) return null

  return (
    <div className="px-4 py-3 border-b border-gray-200 sticky top-[73px] z-10">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Uporabljeni filtri</h3>
        <Button variant="transparent" size="small" onClick={onClearAll} className="text-blue-600 hover:text-blue-800">
          Počisti vse
        </Button>
      </div>
      <div className="max-h-32 overflow-y-auto">
        <ActiveFilters
          filters={displayFilters}
          onRemoveFilter={onRemoveFilter}
          onClearAll={onClearAll}
          categories={categories}
        />
      </div>
    </div>
  )
}

interface FilterActionsProps {
  onSearch: () => void
  onClearAll: () => void
  totalResults: number
  loading: boolean
  hasActiveFilters: boolean
  previewCount: number | null
  isPreviewLoading: boolean
}

const FilterActions = ({ 
  onSearch, 
  onClearAll, 
  totalResults, 
  loading, 
  hasActiveFilters,
  previewCount,
  isPreviewLoading
}: FilterActionsProps) => (
  <div className="p-4 border-t border-gray-200 bg-white sticky bottom-0 z-10 shadow-lg">
    <div className="flex gap-3">
      <Button
        variant="secondary"
        size="large"
        onClick={onClearAll}
        disabled={!hasActiveFilters}
        className="flex-1 h-12 font-medium"
      >
        Počisti vse
      </Button>
      <Button
        variant="primary"
        size="large"
        onClick={onSearch}
        disabled={loading || isPreviewLoading}
        className="flex-2 min-w-0 h-12 bg-blue-600 hover:bg-blue-700"
      >
        <div className="flex items-center gap-2 min-w-0">
          <MagnifyingGlass className="w-4 h-4 flex-shrink-0" />
          <span className="truncate font-medium">
            {loading || isPreviewLoading
              ? "Iskanje..."
              : `Išči • ${(previewCount ?? totalResults).toLocaleString()} rezultatov`}
          </span>
        </div>
      </Button>
    </div>
  </div>
)

interface QuickFiltersProps {
  onQuickFilter: (filterType: string) => void
  filters: SearchFiltersType
}

const QuickFilters = ({ onQuickFilter, filters }: QuickFiltersProps) => {
  const quickFilters = [
    { label: "Najboljša vrednost", value: "best-value", active: filters.priceRange[0] === 0 && filters.priceRange[1] === 500 },
    { label: "Premium možnosti", value: "premium", active: filters.priceRange[0] === 1000 },
    { label: "Samo na zalogi", value: "fast-delivery", active: filters.fastDelivery },
  ]

  return (
    <div className="p-4 border-b border-gray-200 bg-gray-50">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Hitri filtri</h3>
      <div className="flex flex-wrap gap-2">
        {quickFilters.map((filter) => {
          return (
            <Button
              key={filter.value}
              variant={filter.active ? "primary" : "secondary"}
              size="small"
              onClick={() => onQuickFilter(filter.value)}
              className={`text-xs font-medium transition-all ${
                filter.active 
                  ? "bg-blue-600 text-white border-blue-600" 
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {filter.label}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

interface RecentFiltersProps {
  onApplyRecentFilter: (filters: Partial<SearchFiltersType>) => void
}

const RecentFilters = ({ onApplyRecentFilter }: RecentFiltersProps) => {
  const [recentFilters, setRecentFilters] = useState<Array<{
    id: string
    name: string
    filters: Partial<SearchFiltersType>
    timestamp: number
  }>>([])

  useEffect(() => {
    // Load recent filters from localStorage
    const stored = localStorage.getItem('recentFilters')
    if (stored) {
      setRecentFilters(JSON.parse(stored))
    }
  }, [])

  if (recentFilters.length === 0) return null

  return (
    <div className="p-4 border-b border-gray-200">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        Nedavni filtri
      </h3>
      <div className="space-y-2">
        {recentFilters.slice(0, 3).map((recent) => (
          <button
            key={recent.id}
            onClick={() => onApplyRecentFilter(recent.filters)}
            className="w-full text-left p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className="text-sm font-medium text-gray-900">{recent.name}</div>
            <div className="text-xs text-gray-500">
              {new Date(recent.timestamp).toLocaleDateString()}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

interface EnhancedSearchFiltersProps {
  filters: SearchFiltersType
  onFiltersChange: (filters: Partial<SearchFiltersType>) => void
  categories: HttpTypes.StoreProductCategory[]
  facets?: any
  initialFilterOptions: any
  filterOptions: any
  loading: boolean
  showCategoriesFilter?: boolean
}

const EnhancedSearchFilters = ({
  filters,
  onFiltersChange,
  categories,
  facets,
  initialFilterOptions,
  filterOptions,
  loading,
  showCategoriesFilter = true,
}: EnhancedSearchFiltersProps) => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    price: true,
    categories: true,
    brands: false,
    materials: false,
  })
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({})
  const [availablePriceRange, setAvailablePriceRange] = useState({ min: 0, max: 10000 })
  const [priceRangeLoading, setPriceRangeLoading] = useState(false)

  // Fetch available price range when component mounts or filters change
  useEffect(() => {
    const fetchPriceRange = async () => {
      setPriceRangeLoading(true)
      try {
        const range = await getAvailablePriceRange({
          categories: filters.categories,
          brands: filters.brands,
          subcategories: filters.subcategories,
          packaging: filters.packaging,
          orderUnit: filters.orderUnit,
          region: filters.region,
          category: filters.category,
          type: filters.type,
          inStock: filters.inStock,
          fastDelivery: filters.fastDelivery,
          bulkPricing: filters.bulkPricing,
        })
        setAvailablePriceRange(range)
      } catch (error) {
        console.error("Error fetching price range:", error)
      } finally {
        setPriceRangeLoading(false)
      }
    }

    fetchPriceRange()
  }, [filters])

  const handleFilterChange = (
    filterType: keyof SearchFiltersType,
    value: string,
    checked: boolean
  ) => {
    // Special handling for categories to include child categories
    if (filterType === "categories") {
      handleCategoryFilterChange(value, checked)
      return
    }

    const currentValues = (filters[filterType] as string[]) || []
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter((v) => v !== value)

    onFiltersChange({ [filterType]: newValues } as Partial<SearchFiltersType>)
  }

  // New function to handle category filtering with child inclusion
  const handleCategoryFilterChange = (categoryId: string, checked: boolean) => {
    const currentCategories = filters.categories || []
    let newCategories: string[]

    if (checked) {
      // When checking a category, include all its child categories
      const allCategoryIds = getAllCategoryIds(categoryId, categories)
      newCategories = [...new Set([...currentCategories, ...allCategoryIds])]
    } else {
      // When unchecking a category, remove it and all its child categories
      const allCategoryIds = getAllCategoryIds(categoryId, categories)
      newCategories = currentCategories.filter(id => !allCategoryIds.includes(id))
    }

    onFiltersChange({ categories: newCategories } as Partial<SearchFiltersType>)
  }

  const handlePriceRangeChange = (min: number, max: number) => {
    onFiltersChange({ priceRange: [min, max] })
  }

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getFilteredOptions = (sectionKey: string, options: any[]) => {
    const searchTerm = searchTerms[sectionKey] || ""
    if (!searchTerm) return options
    return options.filter((option) =>
      option.value.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  const renderFilterSection = (
    sectionKey: string,
    title: string,
    options: any[],
    isExpanded: boolean
  ) => {
    if (options.length <= 1 && sectionKey !== "price") return null

    return (
      <div key={sectionKey} className="border-b border-gray-200 last:border-b-0">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="flex items-center justify-between w-full p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {options.length > 0 && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                {options.length}
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUpMini className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 max-h-48 overflow-y-auto">
            <div className="space-y-2">
              {options.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={(
                      filters[sectionKey as keyof typeof filters] as string[]
                    ).includes(option.value)}
                    onChange={(e) =>
                      handleFilterChange(
                        sectionKey as keyof typeof filters,
                        option.value,
                        e.target.checked
                      )
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                    disabled={loading}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {option.value}
                    </div>
                    <div className="text-xs text-gray-500">
                      {option.count} izdelkov
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const { mainCategories, subcategoriesByParent } =
    getHierarchicalCategories(categories)

  const formatTitle = (key: string) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
  }

  return (
    <div className="divide-y divide-gray-200">
      {showCategoriesFilter && (
        <div className="border-b border-gray-200 last:border-b-0">
          <button
            onClick={() => toggleSection("categories")}
            className="flex items-center justify-between w-full p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h3 className="font-semibold text-gray-900">Kategorije</h3>
            {expandedSections.categories ? (
              <ChevronUpMini className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          {expandedSections.categories && (
            <div className="px-4 pb-4 max-h-64 overflow-y-auto">
              <div className="space-y-1">
                {mainCategories.map((mainCategory) => {
                  const hasSubcategories = subcategoriesByParent[mainCategory.id]?.length > 0;
                  const isExpanded = expandedCategories[mainCategory.id];
                  return (
                  <div key={mainCategory.id}>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors flex-grow">
                        <input
                          type="checkbox"
                          checked={filters.categories.includes(mainCategory.id)}
                          onChange={(e) =>
                            handleFilterChange(
                              "categories",
                              mainCategory.id,
                              e.target.checked
                            )
                          }
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                          disabled={loading}
                        />
                        <span className="text-sm font-medium text-gray-900">
                          {mainCategory.name}
                        </span>
                      </label>
                      {hasSubcategories && (
                        <button onClick={() => toggleCategoryExpansion(mainCategory.id)} className="p-1">
                            {isExpanded ? <ChevronUpMini className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                    {hasSubcategories && isExpanded && (
                      <div className="pl-6 mt-1 space-y-1">
                        {subcategoriesByParent[mainCategory.id].map(
                          (subcategory) => (
                            <label
                              key={subcategory.id}
                              className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={filters.categories.includes(
                                  subcategory.id
                                )}
                                onChange={(e) =>
                                  handleFilterChange(
                                    "categories",
                                    subcategory.id,
                                    e.target.checked
                                  )
                                }
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                                disabled={loading}
                              />
                              <span className="text-sm text-gray-700">
                                {subcategory.name}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )})}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Price Range */}
      <div className="border-b border-gray-200 last:border-b-0">
        <button
          onClick={() => toggleSection("price")}
          className="flex items-center justify-between w-full p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <h3 className="font-semibold text-gray-900">Cenovni razpon</h3>
          {expandedSections.price ? (
            <ChevronUpMini className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>
        {expandedSections.price && (
          <div className="p-4">
            <PriceRangeSlider
              minPrice={availablePriceRange.min}
              maxPrice={availablePriceRange.max}
              currentMin={filters.priceRange[0]}
              currentMax={filters.priceRange[1]}
              onRangeChange={handlePriceRangeChange}
              loading={loading || priceRangeLoading}
              disabled={loading || priceRangeLoading}
            />
          </div>
        )}
      </div>

      {Object.keys(FILTER_TITLE_MAP).map((sectionKey) => {
        const title =
          FILTER_TITLE_MAP[sectionKey as keyof typeof FILTER_TITLE_MAP]
        const initialOptions =
          initialFilterOptions[sectionKey as keyof typeof initialFilterOptions] || []
        const currentOptions =
          filterOptions[sectionKey as keyof typeof filterOptions] || []
        
        const optionsWithCounts = initialOptions.map((opt: any) => {
          const initialOption = initialFilterOptions?.[sectionKey]?.find((o: any) => o.value === opt.value)
          const current = currentOptions.find((c: any) => c.value === opt.value)
          return {
            ...opt,
            count: initialOption?.count || current?.count || 0
          }
        })

        return renderFilterSection(
          sectionKey,
          title,
          getFilteredOptions(sectionKey, optionsWithCounts),
          expandedSections[sectionKey]
        )
      })}
    </div>
  )
}

export default function MobileFilterDialog({
  isOpen,
  onOpenChange,
  filters,
  onFiltersChange,
  onClearAll,
  categories,
  facets,
  initialFilterOptions,
  filterOptions,
  loading,
  totalResults,
  onSearch,
  showCategoriesFilter,
}: MobileFilterDialogProps) {
  const [localFilters, setLocalFilters] = useState(filters)
  const [previewCount, setPreviewCount] = useState<number | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useSearchParams()
  const query = searchParams.get("q") || ""

  useEffect(() => {
    setLocalFilters(filters)
    setPreviewCount(null) // Reset preview on open
  }, [filters, isOpen])

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    setIsPreviewLoading(true)

    debounceTimeout.current = setTimeout(async () => {
      const params: SearchParams = {
        q: query,
        ...localFilters,
        minPrice: localFilters.priceRange[0],
        maxPrice: localFilters.priceRange[1],
      }
      const count = await getProductsCount(params)
      setPreviewCount(count)
      setIsPreviewLoading(false)
    }, 500)

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [localFilters, query])

  const handleLocalFiltersChange = (newFilters: Partial<SearchFiltersType>) => {
    setLocalFilters(prev => ({ ...prev, ...newFilters }))
  }

  const handleClearAllLocal = () => {
    onClearAll()
    setPreviewCount(null)
  }

  const handleApplyFilters = () => {
    // Save to recent filters
    const recentFilter = {
      id: Date.now().toString(),
      name: generateFilterName(localFilters),
      filters: localFilters,
      timestamp: Date.now()
    }
    
    const existing = JSON.parse(localStorage.getItem('recentFilters') || '[]')
    const updated = [recentFilter, ...existing.slice(0, 1)] // Keep last 2
    localStorage.setItem('recentFilters', JSON.stringify(updated))

    onFiltersChange(localFilters)
    onSearch()
    onOpenChange(false)
  }

  // Handle close with filter application
  const handleClose = () => {
    // Apply current filters before closing
    onFiltersChange(localFilters)
    onSearch()
    onOpenChange(false)
  }

  // Generate a readable name for the filter combination
  const generateFilterName = (filters: SearchFiltersType) => {
    const parts = []
    if (filters.brands.length > 0) parts.push(`${filters.brands.length} brands`)
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 10000) parts.push('price range')
    if (filters.fastDelivery) parts.push('samo na zalogi')
    return parts.join(', ') || 'Nedavni filter'
  }

  // Handle quick filter selection
  const handleQuickFilter = (filterType: string) => {
    let newFilters: Partial<SearchFiltersType> = {}
    
    switch (filterType) {
      case "best-value":
        newFilters = { priceRange: [0, 500] }
        break
      case "premium":
        newFilters = { priceRange: [1000, 10000] }
        break
      case "fast-delivery":
        newFilters = { fastDelivery: !localFilters.fastDelivery }
        break
    }
    
    handleLocalFiltersChange(newFilters)
  }

  // Handle recent filter application
  const handleApplyRecentFilter = (recentFilters: Partial<SearchFiltersType>) => {
    setLocalFilters(prev => ({ ...prev, ...recentFilters }))
  }

  // Check if there are active filters
  const hasActiveFilters = Object.values(localFilters).some(value => {
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'boolean') return value
    if (Array.isArray(value) && value.length === 2) return value[0] > 0 || value[1] < 10000
    return false
  })

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40 animate-in fade-in duration-300" />
        <Dialog.Content className="fixed top-0 right-0 h-full w-full max-w-sm bg-white z-50 flex flex-col animate-in slide-in-from-right duration-300 overflow-x-hidden">
          <Dialog.Description className="sr-only">
            Apply filters to your search results. Use quick filters for common selections or customize your search with detailed options.
          </Dialog.Description>
          
          {/* Filter Header */}
          <FilterHeader
            onClose={handleClose}
            onSearch={handleApplyFilters}
            totalResults={totalResults}
            loading={loading}
          />

          {/* Applied Filters Section */}
          <AppliedFiltersSection
            filters={localFilters}
            onRemoveFilter={handleLocalFiltersChange}
            categories={categories}
            onClearAll={() => {
              setLocalFilters(defaultFilters)
            }}
          />

          {/* Main Filter Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {/* Quick Filters */}
            <QuickFilters onQuickFilter={handleQuickFilter} filters={localFilters} />

            {/* Recent Filters */}
            <RecentFilters onApplyRecentFilter={handleApplyRecentFilter} />

            <EnhancedSearchFilters
              filters={localFilters}
              onFiltersChange={handleLocalFiltersChange}
              categories={categories}
              facets={facets}
              initialFilterOptions={initialFilterOptions}
              filterOptions={filterOptions}
              loading={loading}
              showCategoriesFilter={showCategoriesFilter}
            />
          </div>

          {/* Filter Actions */}
          <FilterActions
            onSearch={handleApplyFilters}
            onClearAll={handleClearAllLocal}
            totalResults={totalResults}
            loading={loading}
            hasActiveFilters={hasActiveFilters}
            previewCount={previewCount}
            isPreviewLoading={isPreviewLoading}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
} 